/**
 * Platform Overview page — how the whole operating system fits together.
 * BR-002 platform architecture · BR-005 blueprint · BR-010 visual system.
 */
import { Link } from "react-router-dom";
import {
  Sparkles, ArrowRight, ChevronRight, Building2, Activity, Layers,
  Brain, LineChart, Users2, LayoutDashboard, Target, Landmark,
  ShieldCheck, KeyRound, ScrollText, GitBranch, Fingerprint, Server,
} from "lucide-react";
import { MarketingHeader, MarketingFooter } from "./MarketingChrome";

const Kicker = ({ children }) => (
  <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">
    {children}
  </div>
);

const Section = ({ children, bg = "white", tid }) => {
  const bgClass = bg === "navy"
    ? "bg-[#0B1F3A] text-white"
    : bg === "slate"
    ? "bg-slate-50 text-slate-900"
    : "bg-white text-slate-900";
  return (
    <section data-testid={tid} className={bgClass}>
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">{children}</div>
    </section>
  );
};

const PIPELINE = [
  { icon: Users2, title: "Lead", desc: "Inbound seller, marketing funnel, or list upload — all land in one intake." },
  { icon: Activity, title: "Analysis", desc: "Engines fire deterministically: ARV, Repair, Score, Market Tier." },
  { icon: Target, title: "Strategy", desc: "8 exit strategies scored in parallel with a numeric Why-Not audit." },
  { icon: Brain, title: "Deal", desc: "Coach synthesises evidence into a next-best-action with citations." },
  { icon: LayoutDashboard, title: "Insight", desc: "Every deal rolls up into the Executive Dashboard for six-dimension health." },
];

const ENGINES = [
  { bp: "BP-002", icon: Building2, title: "Property Workspace", desc: "Plugin tabs + immutable timeline per property." },
  { bp: "BP-005", icon: Sparkles, title: "Deal Score", desc: "0–100 score with cited evidence." },
  { bp: "BP-014", icon: Target, title: "Strategy Comparison", desc: "8 exits scored side-by-side." },
  { bp: "BP-022", icon: Users2, title: "Buyer Intelligence", desc: "Ranked, verified, funded buyers." },
  { bp: "BP-023", icon: Landmark, title: "Portfolio Intelligence", desc: "Cash-flow health for held assets." },
  { bp: "BP-024", icon: LineChart, title: "Market Intelligence", desc: "ZIP-level health, forecast, alerts." },
  { bp: "BP-025", icon: Brain, title: "AI Acquisition Coach", desc: "Grounded next-best-action per property." },
  { bp: "BP-019/020", icon: LayoutDashboard, title: "Executive Dashboard", desc: "40+ KPIs, six dimensions, elite bands." },
  { bp: "BP-026", icon: ShieldCheck, title: "Platform Governance", desc: "RBAC, MFA, IP allowlist, audit log." },
];

const GOVERNANCE = [
  { icon: KeyRound, title: "6-Role RBAC", desc: "Six roles × 26 capabilities. No permission drift." },
  { icon: Fingerprint, title: "MFA + IP Allowlist", desc: "TOTP with backup codes. CIDR-scoped access." },
  { icon: ScrollText, title: "Immutable Audit", desc: "Every action, every mutation, captured forever. Hash-chained." },
  { icon: GitBranch, title: "Versioned Engines", desc: "Every deterministic output cites its BP + version. Zero black boxes." },
  { icon: Server, title: "Tenant Isolation", desc: "Suspendable in one click. Retention policies per tenant." },
  { icon: Sparkles, title: "Explainable AI", desc: "Confidence-capped. Every claim cited. Never invents a number." },
];

export default function PlatformOverviewPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="platform-overview-page">
      <MarketingHeader />

      {/* HERO */}
      <section data-testid="platform-hero" className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              The AI Operating System for Real Estate Investors
            </div>
            <h1 data-testid="platform-hero-headline" className="mt-6 text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
              One platform.<br />
              <span className="text-emerald-400">Nine engines. Every strategy.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">
              iSpeedToClose unifies leads, analysis, strategy, deal execution, and executive insight into a single command surface. Every recommendation is explainable. Every engine is versioned. Every mutation is audited.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/auth?mode=signup"
                data-testid="platform-cta-primary"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
              >
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/products"
                data-testid="platform-cta-secondary"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
              >
                Explore Products
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <Section tid="platform-pipeline" bg="white">
        <Kicker>The Pipeline</Kicker>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
          From lead to insight — one continuous surface.
        </h2>
        <p className="mt-4 text-slate-600 max-w-2xl">
          Every stage of the investor lifecycle flows through the same command surface — no context switches, no data drift, no lost time.
        </p>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-3">
          {PIPELINE.map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="relative rounded-xl border border-slate-200 p-5 bg-white">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="text-xs font-mono text-slate-400">0{i + 1}</div>
              </div>
              <div className="mt-3 font-bold">{title}</div>
              <div className="mt-1 text-xs text-slate-600 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ARCHITECTURE */}
      <Section tid="platform-architecture" bg="slate">
        <Kicker>Architecture</Kicker>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
          Property-centric. Plugin-based. Deterministic.
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { icon: Building2, title: "Property-Centric", desc: "Every entity — buyers, comps, docs, calls, offers — attaches to a Property. One row, one truth." },
            { icon: Activity, title: "Immutable Timeline", desc: "Every field change, every message, every AI recommendation. Hash-chained. Regulator-ready." },
            { icon: Layers, title: "Plugin Tab System", desc: "Analysis, Comps, Docs, Messages, Coach, Marketing, Disposition — mount only what the current stage needs." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-xl bg-white border border-slate-200 p-5">
              <Icon className="w-6 h-6 text-emerald-600" />
              <div className="mt-3 font-bold">{title}</div>
              <div className="mt-2 text-sm text-slate-600">{desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* NINE ENGINES */}
      <Section tid="platform-engines" bg="white">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Kicker>Nine Engines. One Coach.</Kicker>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-2xl">
              Every deterministic engine feeds the same AI Coach.
            </h2>
          </div>
          <Link to="/products" className="text-emerald-600 font-semibold flex items-center gap-1 hover:underline">
            Product deep-dives <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {ENGINES.map(({ icon: Icon, bp, title, desc }) => (
            <div key={bp} className="rounded-xl border border-slate-200 p-5 hover:border-emerald-500 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between">
                <Icon className="w-6 h-6 text-emerald-600" />
                <div className="text-[10px] font-mono text-slate-400">{bp}</div>
              </div>
              <div className="mt-3 font-bold">{title}</div>
              <div className="mt-1 text-sm text-slate-600">{desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* GOVERNANCE */}
      <Section tid="platform-governance" bg="navy">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Kicker>Enterprise Governance</Kicker>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">
              Built for teams that will be audited.
            </h2>
            <p className="mt-4 text-slate-300">
              Every platform action is logged forever. Every mutation optionally gated by TOTP. Every workspace suspendable in one click. Every AI recommendation cites its evidence path. Regulator-ready from day one.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {GOVERNANCE.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-xl bg-white/5 border border-white/10 p-4">
                <Icon className="w-5 h-5 text-emerald-400" />
                <div className="mt-2 font-semibold text-sm">{title}</div>
                <div className="mt-1 text-xs text-slate-400">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* SOLUTIONS BRIDGE */}
      <Section tid="platform-solutions-bridge" bg="slate">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Kicker>Every Strategy Fits</Kicker>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-2xl">
              Same platform. Six purpose-built playbooks.
            </h2>
          </div>
          <Link to="/solutions" className="text-emerald-600 font-semibold flex items-center gap-1 hover:underline">
            All solutions <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {[
            { name: "Wholesalers", to: "/solutions/wholesalers" },
            { name: "Fix & Flip", to: "/solutions/fix-and-flip" },
            { name: "Buy & Hold", to: "/solutions/buy-and-hold" },
            { name: "BRRRR", to: "/solutions/brrrr" },
            { name: "Creative Finance", to: "/solutions/creative-finance" },
            { name: "Teams & Enterprise", to: "/solutions/teams-enterprise" },
          ].map(({ name, to }) => (
            <Link
              key={name}
              to={to}
              data-testid={`platform-solution-${to.split("/").pop()}`}
              className="rounded-lg bg-white border border-slate-200 px-4 py-3 hover:border-emerald-500 hover:shadow-md transition-all flex items-center justify-between"
            >
              <span className="font-semibold text-slate-900">{name}</span>
              <ChevronRight className="w-4 h-4 text-emerald-600" />
            </Link>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <section data-testid="platform-final-cta" className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,150,105,0.22),transparent_70%)]"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            The operating system<br />
            <span className="text-emerald-400">for how real estate is done now.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="platform-final-cta-primary"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
            >
              Book a live demo
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
